import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { SchadenbeteiligterKontaktStatus } from '@gsm/entities';
import { DynamicFormControlEvent, DynamicFormGroupModel, DynamicFormModel } from '@ng-dynamic-forms/core';
import { DynamicMaterialFormComponent } from '@ng-dynamic-forms/ui-material';
import { CustomDynamicFormService } from '../../custom-dynamic-form.service';
import { setModelDisabled } from '../../helpers/disable-model.helper';
import { filterEmptyProps } from '../../helpers/filter-props.helper';
import { formElementsInvalid } from '../../helpers/form-elements-invalid.helper';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { SCHADENBETEILIGTER_KONTAKT_ERFASSEN_TEMPLATE_FACTORY } from './schadenbeteiligter-kontakt-erfassen.config';
import { SchadenbeteiligterKontaktErfassenModel } from './schadenbeteiligter-kontakt-erfassen.model';

const ADRESSE_FIELDS = [
  'schadenbeteiligter_adresse',
  'plz',
  'ort',
  'hausNummer',
  'strasse'
];

@Component({
  selector:    'gsm-schadenbeteiligter-kontakt-erfassen',
  templateUrl: 'schadenbeteiligter-kontakt-erfassen.component.html',
  styleUrls:   ['schadenbeteiligter-kontakt-erfassen.component.scss'],
  imports:     [
    DynamicMaterialFormComponent
  ],
  standalone:  true
})
export class SchadenbeteiligterKontaktErfassenComponent extends CustomDynamicFormControlComponent<SchadenbeteiligterKontaktErfassenModel>
  implements OnInit {
  private formService = inject(CustomDynamicFormService);

  @ViewChild('form', {static: true}) formComponent: DynamicMaterialFormComponent;

  formModel: DynamicFormModel;
  formGroup: UntypedFormGroup;

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  protected onModelChange() {
    const json = SCHADENBETEILIGTER_KONTAKT_ERFASSEN_TEMPLATE_FACTORY(
      this.model.kontaktList,
      this.model.kontaktFormType,
      this.model.additional?.paperProcessOverride
    );

    if (this.model.disabled) {
      setModelDisabled(json, true);
    }

    this.formModel = this.formService.fromJSON(json);
    this.formGroup = this.formService.createFormGroup(this.formModel);

    this._updateFormFromValue();
    this._emitChange();
  }

  protected onDisabledChange() {
    // If enabling and anything other than "ERFASSEN" is selected, exclude adresse fields
    if (!this.model.disabled && this.formGroup.get('schadenbeteiligter_kontakt').value !== 'ERFASSEN') {
      setModelDisabled(this.formModel, false, {excludeIds: ADRESSE_FIELDS});
      // Adresse group needs to be disabled again - seems like a bug.
      setModelDisabled((this.formModel.find(model => model.id === 'schadenbeteiligter_adresse') as DynamicFormGroupModel).group, true);
    } else {
      setModelDisabled(this.formModel, this.model.disabled);
    }
  }

  onFormChange(event: DynamicFormControlEvent): void {
    const schadenbeteiligterKontaktChanged: boolean = !!event.model && event.model.id === 'schadenbeteiligter_kontakt';

    if (schadenbeteiligterKontaktChanged) {
      this.formGroup.get('schadenbeteiligter_name')
        .reset();
      this.formGroup.get('schadenbeteiligter_adresse')
        .reset();
      this.formGroup.get('schadenbeteiligter_kontakt_daten')
        .reset();
    }
    if (schadenbeteiligterKontaktChanged && typeof (event.model as any).value === 'object') {
      this.model.value = {
        ...(event.model as any).value,
        status: 'VERSICHERUNGSNEHMER'
      };
      this._updateFormFromValue();
    } else {
      this._updateValueFromForm();
    }

    this._emitChange();
  }

  private _emitChange() {
    this.group.get(this.model.id)
      .setErrors(this._getErrors());
    this.change.emit(this.model.value);
  }

  private _getErrors(): ValidationErrors {
    const invalid = formElementsInvalid(this.formGroup, this.formModel);
    return invalid ? {schadenbeteiligterKontaktErfassen: true} : null;
  }

  private _updateFormFromValue() {
    if (!this.model.value) {
      return;
    }

    const matchingKontakt = this.model.value.kundeId && this.model.kontaktList.find(item => item.kundeId === this.model.value.kundeId);

    // If matching kontakt exists, get names and adresse from it, otherwise get from model value.
    const {
      vorname,
      nachname,
      kundeId,
      adresse: {
                 plz,
                 ort,
                 hausNummer,
                 strasse
               } = {} as any
    } = matchingKontakt || this.model.value;

    // Get email, telefonNr, notiz from model value if present, otherwise from matching kontakt.
    const [email, telefonNr, notiz] = [
      'email',
      'telefonNr',
      'notiz'
    ].map(key =>
      this.model.value.hasOwnProperty(key) ? this.model.value[key] : matchingKontakt && matchingKontakt[key]
    );

    const status = this.model.value.status;
    const schadenbeteiligterKontakt = kundeId ? this.model.value : !status ? 'ERFASSEN' : status;

    this.formGroup.patchValue({
      schadenbeteiligter_kontakt:       schadenbeteiligterKontakt,
      schadenbeteiligter_name:          {
        vorname,
        nachname
      },
      schadenbeteiligter_adresse:       {
        plz,
        ort,
        hausNummer,
        strasse
      },
      schadenbeteiligter_kontakt_daten: {
        email,
        telefonNr,
        notiz
      }
    });
  }

  private _updateValueFromForm() {
    const schadenbeteiligterKontakt = this.formGroup.get('schadenbeteiligter_kontakt').value;
    const {
      email = '',
      telefonNr = '',
      notiz = ''
    } = this.formGroup.get('schadenbeteiligter_kontakt_daten').value;

    if (!schadenbeteiligterKontakt) {
      this.model.value = null;
    } else if (typeof schadenbeteiligterKontakt === 'string') {
      if (schadenbeteiligterKontakt === 'ERFASSEN') {
        const {
          vorname,
          nachname
        } = this.formGroup.get('schadenbeteiligter_name').value;
        const adresse = this.formGroup.get('schadenbeteiligter_adresse').value;
        this.model.value = filterEmptyProps({
          vorname,
          nachname,
          adresse: filterEmptyProps(adresse),
          email,
          telefonNr,
          notiz,
          status:  'ERFASSEN'
        });
      } else {
        this.model.value = {status: schadenbeteiligterKontakt as SchadenbeteiligterKontaktStatus};
      }
    } else {
      // schadenbeteiligterKontakt is an object -> use its data as basis for model value
      this.model.value = {
        ...schadenbeteiligterKontakt,
        email,
        telefonNr,
        notiz,
        status: 'VERSICHERUNGSNEHMER'
      };
    }
  }
}
